import { CurrencyCode } from '@noths/polaris-dev-ts-types';
import dynamic from 'next/dynamic';

import { trackCarouselNavigation } from 'src/tracking/common/carousel';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import { PlacementContext } from 'src/types/PlacementContext';
import {
  Placement,
  RichRelevanceCarouselProps,
} from '../../../components/blocks/RichRelevanceCarouselBlock';
import { ContentBlockRenderer } from '../../../types/Block';
import { ContentstackSection } from '../../../types/Contentstack';
import { Transformer } from '../../../types/Transformer';
import { mapProductToTrackingProduct } from './mapProductToTrackingProduct';

interface ContentstackRichRelevance extends ContentstackSection {
  placement_type: Placement;
  tracking_title: string;
}

interface ProductCarouselSerializedData {
  trackingTitle: string;
  placementType: Placement;
  currency: CurrencyCode;
  hasDisabledCookies: boolean;
  sessionId?: string;
}

/* istanbul ignore next */
export const renderer: ContentBlockRenderer<
  ProductCarouselSerializedData,
  RichRelevanceCarouselProps
> = {
  Component: dynamic<RichRelevanceCarouselProps>(() =>
    import('../../../components/blocks/RichRelevanceCarouselBlock').then(
      (mod) => mod.RichRelevanceCarouselBlock,
    ),
  ),
  getProps: (serializedData) => {
    const { trackingPosition, placementType, currency } = serializedData;
    const dataForTracking: Omit<PlacementContext, 'trackingTitle'> = {
      placementContentType: 'Rich Relevance',
      trackingPosition,
    };

    return {
      ...serializedData,
      onNavigateCarousel: (swiped, control, { numberOfItems, strategy }) => {
        const trackingDirection = control === 'next' ? 'next' : 'previous';

        trackCarouselNavigation(swiped, trackingDirection, {
          ...dataForTracking,
          numberOfItems,
          trackingTitle: `${strategy} : ${placementType}`,
        });
      },
      onVisible: ({ products, strategy }) => {
        trackProductImpression({
          ...dataForTracking,
          products: products.map((product) => mapProductToTrackingProduct(product, currency)),
          trackingTitle: `${strategy} : ${placementType}`,
        });
      },
      onProductClick: (e, { product, index, numberOfItems, strategy }) => {
        trackProductClick(e, {
          ...dataForTracking,
          product: mapProductToTrackingProduct(product, currency),
          productIndex: index,
          numberOfItems,
          trackingTitle: `${strategy} : ${placementType}`,
        });
      },
    };
  },
};

export const transformer: Transformer<
  ContentstackRichRelevance,
  ProductCarouselSerializedData,
  RichRelevanceCarouselProps
> = (
  { placement_type, _metadata, tracking_title },
  { richRelevanceSessionId, functionalCookiesConsented, currency },
) => ({
  uid: _metadata.uid,
  renderer,
  serializedData: {
    currency,
    hasDisabledCookies: !functionalCookiesConsented,
    placementType: placement_type,
    sessionId: richRelevanceSessionId,
    trackingTitle: tracking_title,
  },
});
